import { useState, useCallback, useMemo } from 'react';

/**
 * Stable pagination state hook that prevents unnecessary re-renders
 * Only manages UI state, not data fetching
 */
export const usePaginationState = (options = {}) => {
    const {
        defaultPage = 1,
        defaultLimit = 20,
        defaultSort = 'id',
        defaultOrder = 'asc',
        defaultSearch = '',
        limitOptions = [10, 20, 50, 100]
    } = options;

    // Core pagination state
    const [page, setPage] = useState(defaultPage);
    const [limit, setLimit] = useState(defaultLimit);
    const [sort, setSort] = useState(defaultSort);
    const [order, setOrder] = useState(defaultOrder);
    const [search, setSearch] = useState(defaultSearch);

    // Stable callback functions - these won't cause re-renders
    const handlePageChange = useCallback((newPage) => {
        setPage(newPage);
    }, []);

    const handleLimitChange = useCallback((newLimit) => {
        setLimit(newLimit);
        setPage(1); // Reset to first page when limit changes
    }, []);

    const handleSortChange = useCallback((newSort, newOrder = 'asc') => {
        setSort(newSort);
        setOrder(newOrder);
        setPage(1); // Reset to first page when sorting changes
    }, []);

    const handleSearchChange = useCallback((newSearch) => {
        setSearch(newSearch);
        setPage(1); // Reset to first page when search changes
    }, []);

    const resetPagination = useCallback(() => {
        setPage(defaultPage);
        setLimit(defaultLimit);
        setSort(defaultSort);
        setOrder(defaultOrder);
        setSearch(defaultSearch);
    }, [defaultPage, defaultLimit, defaultSort, defaultOrder, defaultSearch]);

    // Memoized query parameters object - stable reference
    const queryParams = useMemo(() => ({
        page,
        limit,
        sort,
        order,
        ...(search && { search })
    }), [page, limit, sort, order, search]);

    // Memoized pagination controls object - stable reference
    const paginationControls = useMemo(() => ({
        page,
        limit,
        sort,
        order,
        search,
        limitOptions,
        onPageChange: handlePageChange,
        onLimitChange: handleLimitChange,
        onSortChange: handleSortChange,
        onSearchChange: handleSearchChange,
        resetPagination,
        getQueryParams: () => queryParams
    }), [
        page,
        limit, 
        sort,
        order,
        search,
        limitOptions,
        handlePageChange,
        handleLimitChange,
        handleSortChange,
        handleSearchChange,
        resetPagination,
        queryParams
    ]);

    return paginationControls;
};